import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { Observable, Subscription } from 'rxjs';
import { DATE_RANGE_OPTIONS } from '../../../models/billing-history.models';

/** Workbook layout shared by every export this dialog configures. Matches the
 *  API's `ResourceTimeOffExportLayout` and `RequestExportLayout` enums. */
export type ExportLayout = 'grouped' | 'flattened';

/** Sub-labels rendered under the two layout radio options. */
export interface ExportLayoutDescriptions {
  grouped: string;
  flattened: string;
}

/** Default sub-labels — the resource time off export this dialog was built for. */
export const RESOURCE_TIME_OFF_LAYOUT_DESCRIPTIONS: ExportLayoutDescriptions = {
  grouped: 'One row per resource with time off entries combined',
  flattened: 'One row per time off entry for filtering and analysis',
};

/** Seeds the export-config dialog from the page's current selections so the
 *  user starts from what they're already looking at. */
export interface ExportConfigDialogData {
  layout: ExportLayout;
  /** Sub-labels under the layout options. Defaults to the resource time off wording. */
  layoutDescriptions?: ExportLayoutDescriptions;
  /** Show the "Billing time period" section. Defaults to true; exports without
   *  billing-hours columns (e.g. requests) hide it. */
  showBillingPeriod?: boolean;
  /** Show the "Time off entries" section. Defaults to true; exports that don't
   *  carry time off entries (e.g. requests) hide it. */
  showTimeOffEntries?: boolean;
  /** Noun in the inherited-filters line ("Exporting {subject} matching your
   *  current … filters"). Defaults to "resources". */
  subjectLabel?: string;
  /** Stable DATE_RANGE_OPTIONS.key of the seeded period ('custom' = custom range).
   *  Keyed (not by .days) because calendar options share days: 0. Only read
   *  when the billing period section is shown. */
  dateRangeKey?: string;
  customStartDate?: Date | null;
  customEndDate?: Date | null;
  /** Only read when the time off entries section is shown. */
  statusCodes?: string[];
  timeFrames?: string[];
  /** Label for the view whose list filters the export inherits. */
  viewLabel: string;
  /** Count of inherited list filters, shown in the read-only summary line. */
  activeFilterCount: number;
  /** Runs the export for the chosen config. Emits/completes once the download
   *  has been delivered and errors if it fails, so the dialog can show progress
   *  on its Export button and close itself only after the export finishes. */
  runExport: (config: ExportConfigDialogResult) => Observable<void>;
}

/** The configuration the export should run with; null/undefined when cancelled.
 *  Fields for hidden sections carry their seeded (or empty) values. */
export interface ExportConfigDialogResult {
  layout: ExportLayout;
  dateRangeKey: string;
  customStartDate: Date | null;
  customEndDate: Date | null;
  statusCodes: string[];
  timeFrames: string[];
}

@Component({
  selector: 'app-export-config-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDatepickerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatSelectModule,
  ],
  templateUrl: './export-config-dialog.component.html',
  styleUrls: ['./export-config-dialog.component.scss'],
  animations: [
    // Slide the custom date row out from under the dropdown when Custom is
    // selected, and back up when another option is chosen (mirrors the
    // "add additional days off" expand/collapse in time-off-management).
    trigger('expandCollapse', [
      transition(':enter', [
        style({ height: 0, opacity: 0, marginTop: 0, overflow: 'hidden' }),
        animate(
          '220ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ height: '*', opacity: 1, marginTop: '*' }),
        ),
      ]),
      transition(':leave', [
        style({ overflow: 'hidden' }),
        animate(
          '180ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ height: 0, opacity: 0, marginTop: 0 }),
        ),
      ]),
    ]),
  ],
})
export class ExportConfigDialogComponent {
  dialogRef =
    inject<
      MatDialogRef<ExportConfigDialogComponent, ExportConfigDialogResult>
    >(MatDialogRef);
  data = inject<ExportConfigDialogData>(MAT_DIALOG_DATA);
  private destroyRef = inject(DestroyRef);

  /** True while the export download is in flight — drives the Export button's
   *  spinner and disables the form inputs. */
  isExporting = false;

  private exportSub?: Subscription;

  readonly dateRangeOptions = DATE_RANGE_OPTIONS;

  // Status/time-frame labels mirror the Time Off Requests tab filters
  // (note DENIED is shown as "Returned").
  readonly statusOptions: { value: string; label: string }[] = [
    { value: 'PENDING', label: 'Pending' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'DENIED', label: 'Returned' },
    { value: 'CANCELLED', label: 'Cancelled' },
  ];
  readonly timeFrameOptions: { value: string; label: string }[] = [
    { value: 'PAST', label: 'Past' },
    { value: 'CURRENT', label: 'Current' },
    { value: 'FUTURE', label: 'Future' },
  ];

  readonly layoutDescriptions: ExportLayoutDescriptions;
  readonly showBillingPeriod: boolean;
  readonly showTimeOffEntries: boolean;
  readonly subjectLabel: string;

  layout: ExportLayout;
  dateRangeKey: string;
  customStartDate: Date | null;
  customEndDate: Date | null;
  statusCodes: string[];
  timeFrames: string[];

  constructor() {
    const data = this.data;
    this.layout = data.layout;
    this.layoutDescriptions =
      data.layoutDescriptions ?? RESOURCE_TIME_OFF_LAYOUT_DESCRIPTIONS;
    this.showBillingPeriod = data.showBillingPeriod ?? true;
    this.showTimeOffEntries = data.showTimeOffEntries ?? true;
    this.subjectLabel = data.subjectLabel ?? 'resources';
    this.dateRangeKey = data.dateRangeKey ?? '';
    this.customStartDate = data.customStartDate ?? null;
    this.customEndDate = data.customEndDate ?? null;
    this.statusCodes = [...(data.statusCodes ?? [])];
    this.timeFrames = [...(data.timeFrames ?? [])];
  }

  get isCustomRange(): boolean {
    return this.dateRangeKey === 'custom';
  }

  /** Export is only blocked when a shown custom range is missing one of its endpoints. */
  get isValid(): boolean {
    if (this.showBillingPeriod && this.isCustomRange) {
      return !!this.customStartDate && !!this.customEndDate;
    }
    return true;
  }

  onCancel(): void {
    this.exportSub?.unsubscribe();
    this.dialogRef.close();
  }

  onExport(): void {
    if (!this.isValid || this.isExporting) {
      return;
    }
    this.isExporting = true;
    this.exportSub = this.data
      .runExport({
        layout: this.layout,
        dateRangeKey: this.dateRangeKey,
        customStartDate: this.customStartDate,
        customEndDate: this.customEndDate,
        statusCodes: this.statusCodes,
        timeFrames: this.timeFrames,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        // Close only once the download has been delivered.
        next: () => this.dialogRef.close(),
        // Keep the dialog open so the user can adjust and retry; the parent has
        // already surfaced the failure via a snackbar.
        error: () => {
          this.isExporting = false;
        },
      });
  }
}
